import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from '../auth.service';
import { StoredChatMessage } from './chatbot-conversations.service';

export type AutomationTriggerType = 'schedule' | 'event';
export type AutomationNotifyChannel = 'none' | 'app' | 'email' | 'app_email';
export type AutomationReasoningDepth = 'low' | 'medium' | 'high' | 'max';
export type AutomationRunStatus =
  | 'queued'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'awaiting_confirmation'
  | 'interrupted';

export interface AutomationTriggerConfig {
  cadence?: string;
  time?: string;
  weekdays?: number[];
  monthDay?: number;
  intervalMinutes?: number;
  onceAt?: string;
  hourFrom?: number;
  hourTo?: number;
  type?: string;
  filters?: {
    /** @deprecated legado — preferir businessTypes */
    businessType?: 'electric-mobility' | 'solar-panels';
    businessTypes?: ('electric-mobility' | 'solar-panels')[];
  };
}

export interface AssistantAutomation {
  id: string;
  name: string;
  instructions: string;
  agentIds: string[];
  triggerType: AutomationTriggerType;
  triggerConfig: AutomationTriggerConfig;
  triggerLabel: string;
  autoConfirmWrites: boolean;
  notifyChannel: AutomationNotifyChannel;
  reasoningDepth: AutomationReasoningDepth;
  isActive: boolean;
  nextRunAt: string | null;
  lastRunAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface AssistantAutomationPayload {
  name: string;
  instructions: string;
  agentIds: string[];
  triggerType: AutomationTriggerType;
  triggerConfig: AutomationTriggerConfig;
  autoConfirmWrites: boolean;
  notifyChannel: AutomationNotifyChannel;
  reasoningDepth: AutomationReasoningDepth;
  isActive?: boolean;
}

export interface AssistantAutomationRun {
  id: string;
  automationId: string;
  automationName: string | null;
  conversationId: string | null;
  eventDisplayText?: string | null;
  triggerType: AutomationTriggerType;
  eventType: string | null;
  referenceId: string | null;
  triggerPayload: Record<string, unknown>;
  status: AutomationRunStatus;
  title: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  durationMs: number | null;
  error: string | null;
  createdAt: string | null;
  messages?: StoredChatMessage[];
}

export interface AutomationRunStatsPoint {
  date: string;
  label: string;
  succeeded: number;
  failed: number;
  total: number;
}

export interface AutomationRunStats {
  days: number;
  succeeded: number;
  failed: number;
  total: number;
  series: AutomationRunStatsPoint[];
}

export interface AutomationCatalog {
  cadences: { cadence: string; label: string; description: string; group: string }[];
  events: { type: string; label: string; description: string; group: string }[];
  templates: {
    id: string;
    name: string;
    instructions: string;
    triggerType: AutomationTriggerType;
    triggerConfig: AutomationTriggerConfig;
    agentIds: string[];
    autoConfirmWrites?: boolean;
    notifyChannel?: AutomationNotifyChannel;
    reasoningDepth?: AutomationReasoningDepth;
  }[];
  notifyChannels: { id: AutomationNotifyChannel; label: string }[];
  minIntervalMinutes: number;
}

export interface AutomationRunsPage {
  data: AssistantAutomationRun[];
  pagination: {
    page: number;
    perPage: number;
    total: number;
    totalPages: number;
  };
}

@Injectable({ providedIn: 'root' })
export class AssistantAutomationsService {
  private http = inject(HttpClient);
  private authService = inject(AuthService);
  private baseUrl = `${environment.apiUrl}/admin/assistant-automations`;

  private get headers(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${this.authService.getToken()}`,
    });
  }

  getCatalog(): Observable<AutomationCatalog> {
    return this.http
      .get<{ data: AutomationCatalog }>(`${this.baseUrl}/catalog`, {
        headers: this.headers,
      })
      .pipe(map((res) => res.data));
  }

  list(): Observable<AssistantAutomation[]> {
    return this.http
      .get<{ data: AssistantAutomation[] }>(this.baseUrl, { headers: this.headers })
      .pipe(map((res) => res.data ?? []));
  }

  getById(id: string): Observable<AssistantAutomation> {
    return this.http
      .get<{ data: AssistantAutomation }>(`${this.baseUrl}/${id}`, {
        headers: this.headers,
      })
      .pipe(map((res) => res.data));
  }

  create(payload: AssistantAutomationPayload): Observable<AssistantAutomation> {
    return this.http
      .post<{ data: AssistantAutomation }>(this.baseUrl, payload, {
        headers: this.headers,
      })
      .pipe(map((res) => res.data));
  }

  update(id: string, payload: AssistantAutomationPayload): Observable<AssistantAutomation> {
    return this.http
      .put<{ data: AssistantAutomation }>(`${this.baseUrl}/${id}`, payload, {
        headers: this.headers,
      })
      .pipe(map((res) => res.data));
  }

  delete(id: string): Observable<void> {
    return this.http
      .delete<void>(`${this.baseUrl}/${id}`, { headers: this.headers });
  }

  patchStatus(id: string, isActive: boolean): Observable<AssistantAutomation> {
    return this.http
      .patch<{ data: AssistantAutomation }>(
        `${this.baseUrl}/${id}/status`,
        { isActive },
        { headers: this.headers }
      )
      .pipe(map((res) => res.data));
  }

  listRuns(query: {
    page?: number;
    perPage?: number;
    automationId?: string;
    status?: string;
  }): Observable<AutomationRunsPage> {
    let params = new HttpParams();
    if (query.page) params = params.set('page', String(query.page));
    if (query.perPage) params = params.set('perPage', String(query.perPage));
    if (query.automationId) params = params.set('automationId', query.automationId);
    if (query.status) params = params.set('status', query.status);
    return this.http
      .get<AutomationRunsPage>(`${this.baseUrl}/runs`, {
        headers: this.headers,
        params,
      })
      .pipe(
        map((res) => ({
          data: res.data ?? [],
          pagination: res.pagination ?? {
            page: 1,
            perPage: query.perPage ?? 10,
            total: 0,
            totalPages: 1,
          },
        }))
      );
  }

  getRunStats(): Observable<AutomationRunStats> {
    return this.http
      .get<{ data: AutomationRunStats }>(`${this.baseUrl}/runs/stats`, {
        headers: this.headers,
      })
      .pipe(map((res) => res.data));
  }

  getRun(runId: string): Observable<AssistantAutomationRun> {
    return this.http
      .get<{ data: AssistantAutomationRun }>(`${this.baseUrl}/runs/${runId}`, {
        headers: this.headers,
      })
      .pipe(map((res) => res.data));
  }

  reprocessRun(runId: string): Observable<AssistantAutomationRun> {
    return this.http
      .post<{ data: AssistantAutomationRun }>(
        `${this.baseUrl}/runs/${runId}/reprocess`,
        {},
        { headers: this.headers }
      )
      .pipe(map((res) => res.data));
  }
}
